/**
 * Project lifecycle windows (LEV-2012).
 */

/**
 * How long a soft-deleted project stays restorable. `projects.restore` refuses
 * past this age and the purge job removes the row; both MUST read this
 * constant, or a project becomes unrestorable before it is purged (or, worse,
 * restorable after its data is gone).
 *
 * Boundary: `restore` admits age <= window; the purge takes strictly age >
 * window. The purge lives in `src/lib/projectPurge/purgeProject.ts`, reached
 * from the nightly `cleanupAnonymousProjects` cron and from
 * `purgeProjectOnRequest` ("Delete now"); its compare-and-set claim carries
 * `deletedAt: { lt: cutoff }` in its own where clause rather than trusting the
 * candidate read, so a project exactly 30 days old is never both restorable
 * and purgeable at once.
 */
export const PROJECT_PURGE_WINDOW_DAYS = 30;

/** `PROJECT_PURGE_WINDOW_DAYS` as milliseconds, for timestamp arithmetic. */
export const PROJECT_PURGE_WINDOW_MS = PROJECT_PURGE_WINDOW_DAYS * 24 * 60 * 60 * 1000;

/**
 * Cap on the nightly purge's candidate list. Each candidate costs one Inngest
 * step plus one per-owner flag read, and Inngest caps a single run at 1000
 * steps — an uncapped `findMany` on a backlog would blow that budget in one
 * run instead of failing loudly. `orderBy: deletedAt asc` means the remainder
 * (oldest-first) is simply picked up by tomorrow's run.
 */
export const PROJECT_PURGE_BATCH_SIZE = 500;

/** Where an archive was chosen: the card's Archive item, or "Archive instead" inside the delete confirmation. */
export const PROJECT_ARCHIVE_SOURCES = ["card_menu", "delete_dialog"] as const;
export type ProjectArchiveSource = (typeof PROJECT_ARCHIVE_SOURCES)[number];

/**
 * The lifecycle moves `LoggedInLandingContent` and `LandingProjectSections`
 * both dispatch. `delete` starts the restore window; `purge` ("Delete now" on
 * a Deleted card) skips the rest of it and destroys the project.
 */
export type ProjectLifecycleAction =
  | "archive"
  | "unarchive"
  | "restore"
  | "delete"
  | "purge";

/** `source` records where an archive was chosen; only the delete dialog's "Archive instead" sets it. */
export interface ProjectLifecycleActionOptions {
  source?: ProjectArchiveSource;
}

/**
 * One day in milliseconds. Exported so every surface that measures the restore
 * window — the purge job, `projects.restore`/`purgeNow`, and the home page's
 * "N days left" label — divides by the same number instead of redeclaring it.
 */
export const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whole days since a soft-delete, for the `deletedForDays` analytics property.
 * One definition for the router and the purge, so "deleted for 29 days" means
 * the same thing in `project_restored`, `project_purge_requested` and
 * `project_purged`.
 */
export function deletedForDays(deletedAt: Date, now: Date): number {
  return Math.floor((now.getTime() - deletedAt.getTime()) / DAY_MS);
}
